/**
 * Shared types, helpers, and primitives for the multi-provider usage widget:
 * the compact header rings (UsageBadge) and the detail popover
 * (UsageDetailPanel).
 *
 * Both providers self-gate on the server — ``/api/openrouter-usage`` and
 * ``/api/claude-usage`` each return ``status: "unavailable"`` (without probing
 * upstream) when their key/credential isn't configured — so the widget simply
 * renders whichever come back ``ok``, showing one, both, or neither provider.
 * See cowork/ui/openrouter_usage.py and cowork/ui/claude_usage.py.
 */

export interface UsageMetric {
  utilization: number; // 0-100
  resets_at: string | null; // ISO8601 UTC, or null when unknown
}

export interface ClaudeUsageResponse {
  status: "ok" | "unavailable";
  five_hour: UsageMetric | null;
  seven_day: UsageMetric | null;
  overage: UsageMetric | null;
  limit_status: string | null; // "allowed" | "allowed_warning" | "blocked"
  reason: string | null;
}

export interface OpenRouterUsageResponse {
  status: "ok" | "unavailable";
  usage_usd: number | null; // all-time spend on the key
  usage_window_usd: number | null; // spend in the cap's active reset window
  limit_usd: number | null;
  remaining_usd: number | null;
  usage_pct: number | null; // window spend vs cap (capped keys only)
  limit_reset: string | null; // "daily" | "weekly" | "monthly" | null
  is_free_tier: boolean | null;
  label: string | null;
  reason: string | null;
}

// limit_reset is a cadence token (not a timestamp); map it to the window the
// headline spend covers — matching OpenRouter's own dashboard.
export const RESET_WINDOW_LABEL: Record<string, string> = {
  daily: "today",
  weekly: "this week",
  monthly: "this month",
};

export const POLL_MS = 5 * 60 * 1000;
export const MIN_SPIN_MS = 450;

const WARN_AT = 70;
const DANGER_AT = 90;

export type Severity = "safe" | "warn" | "danger";

export function severityFor(pct: number): Severity {
  if (pct >= DANGER_AT) return "danger";
  if (pct >= WARN_AT) return "warn";
  return "safe";
}

// Brand palette (tailwind.config.ts): oxy teal = safe, gold = warn,
// hydra pink = danger.
export const STROKE_HEX: Record<Severity, string> = {
  safe: "#0FC3BD",
  warn: "#EBBA0B",
  danger: "#EB1771",
};

const TEXT_CLASS: Record<Severity, string> = {
  safe: "text-oxy",
  warn: "text-gold",
  danger: "text-hydra",
};

/** Compact "resets in …" form for tight rows: "2h", "5d", "now". */
export function shortReset(iso: string | null): string {
  if (!iso) return "";
  const resetAt = new Date(iso).getTime();
  if (Number.isNaN(resetAt)) return "";
  const diffMs = resetAt - Date.now();
  if (diffMs <= 0) return "now";
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  return `${Math.round(diffH / 24)}d`;
}

export function fmtUsd(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  // Sub-cent but non-zero spend still reads as $0.00; fine for a glance badge.
  // Larger amounts drop the cents for compactness.
  return n >= 100 ? `$${Math.round(n)}` : `$${n.toFixed(2)}`;
}

/**
 * The single headline percentage for Claude's compact ring: the *binding*
 * (worst-case) utilisation across the 5h / 7d / overage windows — i.e. the
 * constraint closest to its cap, which is what signals risk at a glance.
 */
export function claudeHeadlinePct(d: ClaudeUsageResponse): number {
  return Math.max(
    0,
    d.five_hour?.utilization ?? 0,
    d.seven_day?.utilization ?? 0,
    d.overage?.utilization ?? 0,
  );
}

const RING_SIZE = 30;
const RING_RADIUS = 12;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/** Compact SVG progress ring with the percentage centred inside. */
export function ProgressRing({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, Math.round(pct)));
  const severity = severityFor(clamped);
  const offset = RING_CIRCUMFERENCE * (1 - clamped / 100);
  return (
    <span className={`relative inline-flex ${severity === "danger" ? "animate-pulse" : ""}`}>
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        className="-rotate-90"
      >
        {/* Track — themed via the space-30 token (className, not a hardcoded
            hex) so it goes dark in dark mode instead of staying light-grey. */}
        <circle cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_RADIUS} className="stroke-space-30" strokeWidth={3} fill="none" />
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          stroke={STROKE_HEX[severity]}
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={offset}
          fill="none"
          style={{ transition: "stroke-dashoffset 700ms ease-out, stroke 300ms ease-out" }}
        />
      </svg>
      <span className={`absolute inset-0 flex items-center justify-center text-[8px] font-semibold tabular-nums ${TEXT_CLASS[severity]}`}>
        {clamped}
      </span>
    </span>
  );
}

/** Horizontal usage bar for the detail popover: label · track · % · reset. */
export function UsageBar({
  label,
  pct,
  reset,
}: {
  label: string;
  pct: number;
  reset?: string;
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(pct)));
  const severity = severityFor(clamped);
  return (
    <div className="flex items-center gap-2">
      <span className="w-[4.5rem] shrink-0 text-[10px] text-space-50">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-space-20 overflow-hidden">
        {/* Fill the track and reveal via a left-anchored scaleX so the
            progress transition is GPU-composited (transform/opacity) rather
            than animating ``width``, which would force a layout pass. */}
        <div
          className="h-full w-full rounded-full origin-left"
          style={{
            transform: `scaleX(${clamped / 100})`,
            backgroundColor: STROKE_HEX[severity],
            transition: "transform 700ms ease-out, background-color 300ms ease-out",
          }}
        />
      </div>
      <span className={`w-8 shrink-0 text-right text-[10px] font-semibold tabular-nums ${TEXT_CLASS[severity]}`}>
        {clamped}%
      </span>
      {reset !== undefined && (
        <span className="w-10 shrink-0 text-right text-[10px] text-space-40">{reset}</span>
      )}
    </div>
  );
}
